
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  Mail, 
  Folder, 
  AlertCircle, 
  Clock, 
  HardDrive, 
  RefreshCw,
  ExternalLink,
  Loader2
} from 'lucide-react';

interface EmailTroubleshootingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  onResend: () => Promise<boolean>;
  isResending: boolean;
  cooldownSeconds: number;
}

export function EmailTroubleshootingDialog({
  isOpen,
  onClose,
  email,
  onResend,
  isResending,
  cooldownSeconds
}: EmailTroubleshootingDialogProps) {
  const navigate = useNavigate();

  const troubleshootingSteps = [
    {
      icon: Folder,
      title: 'Controlla la cartella SPAM',
      description: 'L\'email potrebbe essere finita nella cartella Spam o Posta indesiderata'
    },
    {
      icon: Mail,
      title: 'Controlla "Promozioni" o "Altro"',
      description: 'Gmail e altri provider potrebbero averla categorizzata in altre cartelle'
    },
    {
      icon: AlertCircle,
      title: 'Verifica l\'indirizzo email',
      description: `Hai usato: ${email}`
    },
    {
      icon: Clock,
      title: 'Attendi qualche minuto',
      description: 'A volte l\'email può impiegare fino a 5 minuti per arrivare'
    },
    {
      icon: HardDrive,
      title: 'Controlla lo spazio della casella',
      description: 'Assicurati che la tua casella email non sia piena'
    }
  ];

  const handleResend = async () => {
    await onResend();
  };

  const handleRegisterWithDifferentEmail = () => {
    onClose();
    navigate('/auth/register');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
            <Mail className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle className="text-center">Non hai ricevuto l'email?</DialogTitle>
          <DialogDescription className="text-center">
            Ecco alcune possibili cause e soluzioni
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-4">
          {troubleshootingSteps.map((step, index) => (
            <div 
              key={index} 
              className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
            >
              <div className="flex-shrink-0 w-8 h-8 bg-background rounded-full flex items-center justify-center">
                <step.icon className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{step.title}</p>
                <p className="text-xs text-muted-foreground break-words">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t pt-4 space-y-2">
          <p className="text-sm text-muted-foreground text-center">
            Email errata o hai problemi?
          </p>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={handleRegisterWithDifferentEmail}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Registrati con un'altra email
          </Button>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Chiudi
          </Button>
          <Button
            onClick={handleResend}
            disabled={isResending || cooldownSeconds > 0}
            className="w-full sm:w-auto"
          >
            {isResending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Invio in corso...
              </>
            ) : cooldownSeconds > 0 ? (
              `Attendi ${cooldownSeconds}s`
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reinvia email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
